import { useMemo } from "react";
import { useHotkeys } from "react-hotkeys-hook";

import { ampli } from "@/config";
import { DRIVER_SHORTCUTS, type DriverShortcutId } from "./driverShortcuts";

/** Imperative handlers the driver-table shortcuts invoke; supplied by `DriverView`. */
export interface DriverShortcutActions {
  moveHighlight: (delta: 1 | -1) => void;
  focusSearch: () => void;
  toggleChannel: () => void;
  openComposer: () => void;
  /** Toggle selection of the highlighted row; `range` extends from the last selection anchor (Shift). */
  toggleSelect: (range: boolean) => void;
  /** Move the highlight by `delta` while selecting every row between the anchor and the new highlight. */
  extendSelection: (delta: 1 | -1) => void;
  toggleUnread: () => void;
  fleetMessage: () => void;
  markRead: () => void;
  showShortcuts: () => void;
  escape: (event: KeyboardEvent) => void;
}

/**
 * Whether a button or link currently holds focus. `Enter` would natively activate it, so the `Enter`/`O` channel
 * toggle defers to that control (a11y). During row navigation `DriverView` blurs such controls, so this predicate is
 * true only when the user is deliberately on a button rather than navigating the table.
 */
function isActivatableElementFocused(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  return el.tagName === "BUTTON" || el.tagName === "A" || el.getAttribute("role") === "button";
}

/** `useKey` matching ignores modifiers, so guard the character-matched shortcuts against Cmd/Ctrl/Alt (out of scope). */
function hasPrimaryModifier(event: KeyboardEvent): boolean {
  return event.metaKey || event.ctrlKey || event.altKey;
}

/**
 * Whether a MUI menu/popover/select overlay is open (team dropdown, column filters, header context menu, etc.). These
 * portal their content and keep focus on `role="menuitem"`/list elements that pass the form-tag guard, so table
 * shortcuts would otherwise fire behind them. Popovers/menus only mount while open, so this is false at rest.
 */
function isMenuOverlayOpen(): boolean {
  return document.querySelector(".MuiPopover-root, .MuiMenu-root") != null;
}

/**
 * Binds the Gmail-style driver-table shortcuts to their handlers using the shared {@link DRIVER_SHORTCUTS} registry.
 *
 * Single-key shortcuts stay inert while a text input, textarea, or contenteditable is focused (react-hotkeys-hook's
 * default form-tag handling) and while `enabled` is false — e.g. when the shortcuts pane or fleet-message modal is
 * open. Escape is always live and fires inside inputs so it can blur them as the first step of its back-out order.
 * Every action shortcut emits `keyboardShortcut` (action label + keystroke) before running.
 *
 * @param actions - Handlers invoked when each shortcut fires. The latest closures are always used.
 * @param enabled - Whether the table shortcuts are active; Escape ignores this and is always bound.
 */
export function useDriverShortcuts(actions: DriverShortcutActions, enabled: boolean): void {
  const tableOptions = useMemo(
    () => ({ enabled, preventDefault: true, ignoreEventWhen: isMenuOverlayOpen }),
    [enabled],
  );
  // `Enter`/`O` defer to a focused button/link so its native activation still fires (e.g. reopening the shortcuts
  // pane from the header icon). Row navigation blurs stray focus first, so this only stands down for deliberate focus.
  const channelToggleOptions = useMemo(
    () => ({
      enabled,
      preventDefault: true,
      ignoreEventWhen: (_e: KeyboardEvent) => isMenuOverlayOpen() || isActivatableElementFocused(),
    }),
    [enabled],
  );
  // `/` and `?` are punctuation that differ by keyboard layout and Shift state, so match them by the produced
  // character (`useKey`). `useKey` skips modifier checks, so keep Cmd/Ctrl/Alt combos (Cmd+S etc.) out of scope.
  const keyMatchedOptions = useMemo(
    () => ({
      enabled,
      preventDefault: true,
      useKey: true,
      ignoreEventWhen: (e: KeyboardEvent) => isMenuOverlayOpen() || hasPrimaryModifier(e),
    }),
    [enabled],
  );
  const escapeOptions = useMemo(() => ({ enableOnFormTags: true, enableOnContentEditable: true }), []);

  // Emit the analytics event with the shortcut's helper-pane label as the action and the exact keystroke pressed.
  const fire = (id: DriverShortcutId, keystroke: string, run: () => void) => {
    ampli.keyboardShortcut({ action: DRIVER_SHORTCUTS[id].label, keystroke });
    run();
  };

  useHotkeys(
    DRIVER_SHORTCUTS.moveDown.hotkeys,
    (_e, hk) => fire("moveDown", hk.hotkey, () => actions.moveHighlight(1)),
    tableOptions,
  );
  useHotkeys(
    DRIVER_SHORTCUTS.moveUp.hotkeys,
    (_e, hk) => fire("moveUp", hk.hotkey, () => actions.moveHighlight(-1)),
    tableOptions,
  );
  useHotkeys(
    DRIVER_SHORTCUTS.focusSearch.hotkeys,
    (_e, hk) => fire("focusSearch", hk.hotkey, actions.focusSearch),
    keyMatchedOptions,
  );
  useHotkeys(
    DRIVER_SHORTCUTS.toggleChannel.hotkeys,
    (_e, hk) => fire("toggleChannel", hk.hotkey, actions.toggleChannel),
    channelToggleOptions,
  );
  useHotkeys(
    DRIVER_SHORTCUTS.openComposer.hotkeys,
    (_e, hk) => fire("openComposer", hk.hotkey, actions.openComposer),
    tableOptions,
  );
  useHotkeys(
    DRIVER_SHORTCUTS.toggleSelect.hotkeys,
    (event, hk) => fire("toggleSelect", hk.hotkey, () => actions.toggleSelect(event.shiftKey)),
    tableOptions,
  );
  useHotkeys(
    DRIVER_SHORTCUTS.extendSelectionDown.hotkeys,
    (_e, hk) => fire("extendSelectionDown", hk.hotkey, () => actions.extendSelection(1)),
    tableOptions,
  );
  useHotkeys(
    DRIVER_SHORTCUTS.extendSelectionUp.hotkeys,
    (_e, hk) => fire("extendSelectionUp", hk.hotkey, () => actions.extendSelection(-1)),
    tableOptions,
  );
  useHotkeys(
    DRIVER_SHORTCUTS.toggleUnread.hotkeys,
    (_e, hk) => fire("toggleUnread", hk.hotkey, actions.toggleUnread),
    tableOptions,
  );
  useHotkeys(
    DRIVER_SHORTCUTS.fleetMessage.hotkeys,
    (_e, hk) => fire("fleetMessage", hk.hotkey, actions.fleetMessage),
    tableOptions,
  );
  useHotkeys(
    DRIVER_SHORTCUTS.markRead.hotkeys,
    (_e, hk) => fire("markRead", hk.hotkey, actions.markRead),
    tableOptions,
  );
  // `?` emits keyboardShortcut like every other binding; the handler additionally emits viewKeyboardShortcuts on open.
  useHotkeys(
    DRIVER_SHORTCUTS.showShortcuts.hotkeys,
    (_e, hk) => fire("showShortcuts", hk.hotkey, actions.showShortcuts),
    keyMatchedOptions,
  );

  // Escape stays live regardless of `enabled` and fires inside inputs so it can blur the focused field first.
  useHotkeys(DRIVER_SHORTCUTS.escape.hotkeys, (event) => actions.escape(event), escapeOptions);
}
